import {
  Flex, Box, Text, Button
} from '@huspy/briks-web';
import { useGetBaseRates } from '@modules/banks/hooks/queries/useGetBaseRates';
import { SimpleGrid, Skeleton } from '@mantine/core';
import { BaseSlido } from '@modules/core/components/BaseSlido';
import { useDisclosure } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';
import { t as i18n } from 'i18next';
import { convertToDDMMYYYYFormat, formatToLocalTimeZone } from '@shared/utils';
import { IconTrendingUp } from '@tabler/icons-react';
import { rateInvidualCard } from './styles';

const RATES: Record<string, string> = {
  'EIBOR OVERNIGHT': i18n('bankProducts.interbankRate.rates.overnight'),
  'EIBOR 1 WEEK': i18n('bankProducts.interbankRate.rates.1week'),
  'EIBOR 1 MONTH': i18n('bankProducts.interbankRate.rates.1month'),
  'EIBOR 3 MONTH': i18n('bankProducts.interbankRate.rates.3month'),
  'EIBOR 6 MONTH': i18n('bankProducts.interbankRate.rates.6month'),
  'EIBOR 12 MONTH': i18n('bankProducts.interbankRate.rates.12month'),
};

const Rate = ({ rateName, value }: { rateName: string, value: number }) => (
  <Flex flex={ 1 } direction='column'>
    <Flex className={ rateInvidualCard.header }>
      <Text size='md' color='text-primary_on-brand'>
        {rateName}
      </Text>
    </Flex>
    <Flex flex={ 1 } className={ rateInvidualCard.content } direction='column'>
      <Text size='3xl' weight='semiBold' color='text-primary'>{value}</Text>
    </Flex>
  </Flex>
);

export const InterbankRate = ({ variant = 'secondary' }: { variant?: 'tertiary' | 'secondary' }) => {
  const { data, isLoading } = useGetBaseRates();
  const [isOpen, { open, close }] = useDisclosure(false);

  const { t } = useTranslation();

  if (isLoading || !data) {
    return <Skeleton w='200px' h='52px' radius='.5rem' />;
  }

  const onRate = data.find((_rate) => _rate.base_rate_type === 'EIBOR OVERNIGHT');

  const order = Object.keys(RATES);

  return (
    <>
      <Button onClick={ open } variant={ variant }>
        <IconTrendingUp />
        { t('bankProducts.interbankRate.cta') }
      </Button>

      <BaseSlido
        opened={ isOpen }
        close={ close }
        title={ (
          <Flex p={ 4 } direction='column' gap={ 2 }>
            <Text size='5xl'>{ t('bankProducts.interbankRate.title') }</Text>

            { onRate?.start_date
              && (
                <Text size='md' color='neutral.500'>
                  { t('bankProducts.interbankRate.lastUpdated') }
                  {' '}
                  { convertToDDMMYYYYFormat(formatToLocalTimeZone(onRate?.start_date)) }
                </Text>
              )}
          </Flex>
        ) }
        footer={ (
          <Flex justify='flex-end'>
            <Button
              onClick={ close }
            >
              { t('bankProducts.interbankRate.close') }
            </Button>
          </Flex>
        ) }
      >
        <Box p={ 4 } h='100%'>
          <SimpleGrid cols={ 3 } spacing={ 15 }>
            { data
              .sort((a, b) => order.indexOf(a.base_rate_type) - order.indexOf(b.base_rate_type))
              .map((individualRate) => (
                <Rate
                  key={ individualRate.base_rate_type }
                  rateName={ RATES[individualRate.base_rate_type] ?? individualRate.base_rate_type }
                  value={ individualRate.value }
                />
              ))}
          </SimpleGrid>
        </Box>
      </BaseSlido>
    </>
  );
};
